import React, { useState, useEffect, CSSProperties } from 'react';
import { VERSION } from '@/common/constants/api';
import { FileTypes } from '@/common/constants/filetypes';
import { AppConfig } from '@/Config';
import { authenticatedFetch } from '@/common/utils/authFetch';
import MobilePDFViewer from './MobilePDFViewer';
import { Icon } from 'semantic-ui-react';
import { getCurrentOrganizationId } from '@/common/selectors/auth';
import { useSelector } from 'react-redux';
import { getYouTubeEmbedUrl } from '@/common/utils/resourceLinks';

export interface MobileFileViewerProps {
  fileId: string;
  style?: CSSProperties;
  isFullScreen?: boolean;
}

const MobileFileViewer: React.FC<MobileFileViewerProps> = ({ fileId, style, isFullScreen }) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [resourceUrl, setResourceUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const organizationId = useSelector(getCurrentOrganizationId);

  useEffect(() => {
    setFileUrl(null);
    setBlob(null);
    setFileType(null);
    setResourceUrl(null);
    setError(null);

    const fetchFile = async () => {
      try {
        const headers: HeadersInit = {};
        if (organizationId) {
          headers['X-NULOGIC-ORGANIZATION-ID'] = organizationId.toString();
        }

        const apiUrl = AppConfig.api.apiUrl || '';
        let linkUrl: string | null = null;

        try {
          const metadataResponse = await authenticatedFetch(
            `${apiUrl}/api/portal/${VERSION}/repository-files/${fileId}/`,
            { method: 'GET', headers }
          );

          if (metadataResponse.ok) {
            const metadata = await metadataResponse.json();
            if (
              metadata.type === FileTypes.URL ||
              metadata.type === 'link' ||
              metadata.type === 'url'
            ) {
              linkUrl = metadata.object_key || metadata.url || null;
            }
          }
        } catch {
          // ignore metadata fetch failure and fall back to stream
        }

        if (linkUrl) {
          setFileType('link');
          setResourceUrl(linkUrl);
          return;
        }

        const response = await authenticatedFetch(
          `${apiUrl}/api/portal/${VERSION}/repository-files/${fileId}/stream`,
          { method: 'GET', headers }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch file.');
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename: string | null = null;
        if (contentDisposition) {
          const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
          if (rfc5987Match && rfc5987Match.length > 1) {
            try {
              filename = decodeURIComponent(rfc5987Match[1]);
            } catch {
              // ignore decode errors
            }
          }

          if (!filename) {
            const quotedMatch = contentDisposition.match(/filename="(.+?)"/);
            if (quotedMatch && quotedMatch.length > 1) {
              filename = quotedMatch[1];
            } else {
              const unquotedMatch = contentDisposition.match(/filename=([^;]+)/);
              if (unquotedMatch && unquotedMatch.length > 1) {
                filename = unquotedMatch[1].trim().replace(/^['"]|['"]$/g, '');
              }
            }
          }
        }

        if (!filename) {
          throw new Error('Failed to determine file type.');
        }

        const contentType = filename.split('.').pop();
        if (!contentType) {
          throw new Error('Failed to determine file type.');
        }

        setFileType(contentType);
        const fileBlob = await response.blob();
        setBlob(fileBlob);

        if (contentType === 'mp4') {
          setFileUrl(URL.createObjectURL(fileBlob));
        }

        if (contentType === 'link') {
          setResourceUrl(filename);
        }
      } catch (err: any) {
        setError(err.message || 'Error loading file.');
      }
    };

    fetchFile();

    return () => {
      setFileUrl(prevUrl => {
        if (prevUrl) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    };
  }, [fileId, organizationId]);

  if (error) {
    return <p className="error">Error: {error}</p>;
  }

  if (!fileType || (fileType === 'mp4' && !fileUrl) || (fileType === 'pdf' && !blob) || (fileType === 'link' && !resourceUrl)) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '16px',
        }}
      >
        <Icon name="spinner" loading size="large" />
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...style,
      }}
    >
      {fileType === 'pdf' ? (
        <MobilePDFViewer
          blob={blob}
          isFullScreen={isFullScreen ?? false}
          viewMode="fitWidth"
          scale={1}
        />
      ) : fileType === 'mp4' ? (
        <div style={{ width: '100%', height: '100%' }}>
          <video
            controls
            src={fileUrl ?? undefined}
            style={{ width: '100%', height: '100%', borderRadius: '16px', background: '#000' }}
          />
        </div>
      ) : fileType === 'link' ? (
        (() => {
          const youtubeEmbedUrl = getYouTubeEmbedUrl(resourceUrl!);
          if (youtubeEmbedUrl) {
            return (
              <iframe
                title="YouTube Preview"
                src={youtubeEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 'none', minWidth: '100%', minHeight: '100%' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            );
          }

          return (
            <div style={{ width: '100%', height: '100%', padding: '1rem', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <p style={{ marginBottom: '1rem' }}>Unable to preview this link on mobile.</p>
              <a href={resourceUrl ?? undefined} target="_blank" rel="noopener noreferrer">
                Open link in browser
              </a>
            </div>
          );
        })()
      ) : (
        <div style={{ width: '100%', height: '100%', padding: '1rem' }}>
          <p>Unsupported file type: {fileType}</p>
          {fileUrl && (
            <a href={fileUrl} download>
              Download File
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileFileViewer;
